/**
 * @file 学员管理页
 * @description 侧栏「学员管理」：接口列表（按科目展平）、可折叠筛选与行操作。
 *
 * TODO: 重新测试后端暂无对应接口（详见 docs/学员管理接口缺口.md）。
 *
 * @whenToUse 路由 `/students`。
 * @whenNotToUse 其他业务菜单页。
 */

import { useRef, useState } from 'react'
import { MoreHorizontal, Pencil, Plus } from 'lucide-react'
import { confirm } from '@/components/ui/confirm.tsx'
import { Badge, type BadgeVariant } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  computeConsecutiveRowSpan,
  type DataTableColumn,
} from '@/components/ui/data-table.tsx'
import { DropdownMenu } from '@/components/ui/dropdown-menu.tsx'
import { IconButton } from '@/components/ui/icon-button.tsx'
import { PageHeader } from '@/components/ui/page-header.tsx'
import {
  RequestTable,
  type RequestTableHandle,
  type RequestTableResult,
} from '@/components/ui/request-table.tsx'
import { toast } from '@/components/ui/toast.tsx'
import {
  bindStudentTeacher,
  changeStudentPoints,
  changeStudentStatus,
  createStudent,
  deleteStudent,
  fetchStudentList,
  resetStudentPwd,
  setStudentAllowEditBasicInfo,
  updateStudent,
  type StudentItem,
  type StudentListParams,
  type StudentSubjectPayload,
} from '@/api/student.ts'
import { useUserStore } from '@/stores/user-store.ts'
import { StudentBindTeacherDialog } from '@/components/pages/student-page/student-bind-teacher-dialog.tsx'
import { StudentFormDialog } from '@/components/pages/student-page/student-form-dialog.tsx'
import {
  StudentPointsDialog,
  type StudentPointsDialogMode,
} from '@/components/pages/student-page/student-points-dialog.tsx'
import { StudentRechargeRecordsDialog } from '@/components/pages/student-page/student-recharge-records-dialog.tsx'
import { StudentSearchFields } from '@/components/pages/student-page/student-search-fields.tsx'
import {
  EMPTY_STUDENT_QUERY,
  GRADE_OPTIONS,
  LEARNING_LEVEL_OPTIONS,
  SEMESTER_OPTIONS,
  STAGE_OPTIONS,
  STUDENT_STATUS_LABEL,
  SUBJECT_OPTIONS,
  flattenStudentRows,
  optionLabel,
  type StudentFormValues,
  type StudentListRow,
  type StudentQuery,
  type StudentStatus,
} from '@/components/pages/student-page/types.ts'

const STATUS_BADGE: Record<StudentStatus, BadgeVariant> = {
  '1': 'success',
  '2': 'warning',
  '0': 'danger',
}

export function StudentPage() {
  const isMainAccount = useUserStore((s) => s.user?.isMain) === '1'
  const tableRef = useRef<RequestTableHandle>(null)
  /** 最近一页学员原对象（展平行缺 subjectList，编辑时按 id 回查） */
  const studentsRef = useRef(new Map<string, StudentItem>())
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<StudentItem | null>(null)
  const [pointsOpen, setPointsOpen] = useState(false)
  const [pointsMode, setPointsMode] =
    useState<StudentPointsDialogMode>('recharge')
  const [pointsTarget, setPointsTarget] = useState<StudentListRow | null>(null)
  const [recordsOpen, setRecordsOpen] = useState(false)
  const [recordsTarget, setRecordsTarget] = useState<StudentListRow | null>(null)
  const [bindOpen, setBindOpen] = useState(false)
  const [bindTarget, setBindTarget] = useState<StudentListRow | null>(null)

  /** 行操作成功后刷新列表 */
  const refresh = () => tableRef.current?.refresh()

  /**
   * 请求前透传后端支持的筛选项
   * @param params 表单查询条件 + 分页
   */
  const beforeRequest = (params: StudentQuery & {
    pageNum?: number
    pageSize?: number
  }): StudentListParams => ({
    pageNum: params.pageNum,
    pageSize: params.pageSize,
    studentName: params.studentName || undefined,
    status: params.status || undefined,
    createTimeStart: params.createTimeRange.from || undefined,
    createTimeEnd: params.createTimeRange.to || undefined,
    subject: params.subject || undefined,
    textbookId: params.textbook || undefined,
    educationStage: params.educationStage || undefined,
    grade: params.grade || undefined,
    semester: params.semester || undefined,
    learningGrade: params.learningGrade || undefined,
    learningForce: params.learningForce,
  })

  /**
   * 响应后按科目展平（total 仍按学员数分页）
   * @param response 列表响应
   */
  const afterRequest = (
    response: RequestTableResult<StudentItem>,
  ): RequestTableResult<StudentListRow> => {
    studentsRef.current = new Map(
      response.rows.map((item) => [item.id, item]),
    )
    return {
      rows: flattenStudentRows(response.rows),
      total: response.total,
    }
  }

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  /**
   * 表单值转提交的科目详情集合
   * @param values 表单值
   */
  const toSubjectPayload = (
    values: StudentFormValues,
  ): StudentSubjectPayload[] =>
    values.details.map((item) => ({
      id: item.id,
      subject: item.subject,
      textbookId: item.textbookId,
      educationStage: item.educationStage,
      grade: item.grade,
      semester: item.semester,
      learningGrade: item.learningGrade,
      learningForce: item.learningForce,
      teacherId: item.teacherId || undefined,
    }))

  const handleFormSubmit = async (values: StudentFormValues) => {
    try {
      if (editing) {
        await updateStudent({
          id: editing.id,
          studentName: values.studentName.trim(),
          phone: values.phone.trim(),
          points: values.points,
          subjectList: toSubjectPayload(values),
        })
        toast.success('已保存学员', values.studentName)
      } else {
        await createStudent({
          studentName: values.studentName.trim(),
          phone: values.phone.trim(),
          points: values.points,
          password: values.password.trim() || undefined,
          subjectList: toSubjectPayload(values),
        })
        toast.success(
          '已新增学员',
          values.password.trim() ? undefined : '密码默认 123456',
        )
      }
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '保存失败')
    }
  }

  /**
   * 打开充值 / 扣除弹窗
   * @param row 学员维度展平行
   * @param mode 充值或扣除
   */
  const openPointsDialog = (
    row: StudentListRow,
    mode: StudentPointsDialogMode,
  ) => {
    setPointsTarget(row)
    setPointsMode(mode)
    setPointsOpen(true)
  }

  /**
   * 确认充值 / 扣除
   * @param points 变动点数（正数）
   * @param remark 备注
   */
  const handlePointsConfirm = async (points: number, remark: string) => {
    if (!pointsTarget) return
    const delta = pointsMode === 'deduct' ? -points : points
    try {
      await changeStudentPoints(pointsTarget.studentId, delta, remark || undefined)
      toast.success(
        pointsMode === 'deduct' ? '扣除成功' : '充值成功',
        `${delta > 0 ? '+' : ''}${delta} 点`,
      )
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '操作失败')
    }
  }

  /**
   * 重置密码（二次确认）
   * @param row 学员维度展平行
   */
  const handleResetPassword = async (row: StudentListRow) => {
    const ok = await confirm({
      title: '重置密码？',
      description: `将「${row.studentName}」账号 ${row.phone} 的密码重置为 123456，确认是否重置？`,
      confirmText: '重置',
    })
    if (!ok) return
    try {
      await resetStudentPwd(row.studentId)
      toast.success('密码已重置为 123456', row.studentName)
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '重置失败')
    }
  }

  const handleDelete = async (row: StudentListRow) => {
    const ok = await confirm({
      title: '删除学员？',
      description: `将删除「${row.studentName}」，此操作不可恢复。`,
      confirmText: '删除',
      danger: true,
    })
    if (!ok) return
    try {
      await deleteStudent(row.studentId)
      toast.success('已删除', row.studentName)
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '删除失败')
    }
  }

  /**
   * 启用 / 禁用学员（二次确认）
   * @param row 学员维度展平行
   */
  const handleToggleStatus = async (row: StudentListRow) => {
    const willEnable = row.status === '0'
    const nextStatus: '0' | '1' = willEnable ? '1' : '0'
    const ok = await confirm({
      title: willEnable ? '启用学员？' : '禁用学员？',
      description: willEnable
        ? `启用后「${row.studentName}」可正常使用，确认启用？`
        : `禁用后「${row.studentName}」将无法正常使用，确认禁用？`,
      confirmText: willEnable ? '启用' : '禁用',
      danger: !willEnable,
    })
    if (!ok) return
    try {
      await changeStudentStatus(row.studentId, nextStatus)
      toast.success(willEnable ? '已启用' : '已禁用', row.studentName)
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '操作失败')
    }
  }

  /**
   * 允许 / 禁止调整基本信息（二次确认；仅主账号可操作）
   * @param row 学员维度展平行
   */
  const handleToggleBasicInfo = async (row: StudentListRow) => {
    const willAllow = row.allowEditBasicInfo !== 1
    const ok = await confirm({
      title: willAllow ? '允许调整基本信息？' : '禁止调整基本信息？',
      description: willAllow
        ? `确认允许「${row.studentName}」在 PAD 端调整基本信息？`
        : `确认禁止「${row.studentName}」在 PAD 端调整基本信息？`,
      confirmText: willAllow ? '允许' : '禁止',
    })
    if (!ok) return
    try {
      await setStudentAllowEditBasicInfo(row.studentId, willAllow ? 1 : 0)
      toast.success(
        willAllow ? '已允许调整基本信息' : '已禁止调整基本信息',
        row.studentName,
      )
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '操作失败')
    }
  }

  /**
   * 确认绑定 / 修改老师
   * @param teacherId 教师 id；空串表示解绑
   */
  const handleBindTeacherConfirm = async (teacherId: string) => {
    if (!bindTarget) return
    try {
      await bindStudentTeacher(bindTarget.id, teacherId || null)
      toast.success('已更新老师')
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '操作失败')
    }
  }

  /** 同一学员多科目行：学员维度列纵向合并 */
  const studentRowSpan: DataTableColumn<StudentListRow>['onCell'] = (
    _record,
    index,
    data,
  ) => ({
    rowSpan: computeConsecutiveRowSpan(
      data,
      index,
      (row) => row.studentId,
    ),
  })

  const columns: DataTableColumn<StudentListRow>[] = [
    {
      key: 'studentName',
      title: '学员名称',
      dataIndex: 'studentName',
      minWidth: 100,
      fixed: 'left',
      onCell: studentRowSpan,
    },
    {
      key: 'phone',
      title: '手机号',
      dataIndex: 'phone',
      minWidth: 120,
      onCell: studentRowSpan,
    },
    {
      key: 'points',
      title: '点数',
      dataIndex: 'points',
      minWidth: 72,
      onCell: studentRowSpan,
    },
    {
      key: 'subject',
      title: '科目',
      minWidth: 72,
      className: 'border-l border-border',
      render: (row) => optionLabel(SUBJECT_OPTIONS, row.subject),
    },
    {
      key: 'textbook',
      title: '教材',
      dataIndex: 'textbookName',
      minWidth: 120,
    },
    {
      key: 'grade',
      title: '年级',
      minWidth: 72,
      render: (row) => optionLabel(GRADE_OPTIONS, row.grade),
    },
    {
      key: 'semester',
      title: '学期',
      minWidth: 72,
      render: (row) => optionLabel(SEMESTER_OPTIONS, row.semester),
    },
    {
      key: 'stage',
      title: '阶段',
      minWidth: 72,
      render: (row) => optionLabel(STAGE_OPTIONS, row.educationStage),
    },
    {
      key: 'learningLevel',
      title: '学习力等级',
      minWidth: 96,
      render: (row) =>
        optionLabel(LEARNING_LEVEL_OPTIONS, row.learningGrade),
    },
    {
      key: 'learningPower',
      title: '学习力',
      dataIndex: 'learningForce',
      minWidth: 72,
    },
    {
      key: 'teacherName',
      title: '所属教师',
      minWidth: 120,
      render: (row) => (
        <button
          type="button"
          className="inline-flex max-w-full items-center gap-1.5 text-left text-primary transition-opacity duration-150"
          aria-label={`修改${row.studentName}的老师`}
          onClick={() => {
            setBindTarget(row)
            setBindOpen(true)
          }}
        >
          <Pencil className="size-3.5 shrink-0 text-primary" aria-hidden />
          <span className="truncate">{row.teacherName || '未绑定'}</span>
        </button>
      ),
    },
    {
      key: 'retest',
      title: '重新测试',
      minWidth: 96,
      render: (row) => (
        <button
          type="button"
          className="text-primary transition-opacity duration-150 hover:opacity-90"
          aria-label={`重新测试${row.studentName}`}
          onClick={() => {
            toast.info('功能开发中')
          }}
        >
          重新测试
        </button>
      ),
    },
    {
      key: 'status',
      title: '状态',
      minWidth: 88,
      className: 'border-l border-border',
      onCell: studentRowSpan,
      render: (row) => (
        <Badge variant={STATUS_BADGE[row.status]}>
          {STUDENT_STATUS_LABEL[row.status]}
        </Badge>
      ),
    },
    {
      key: 'createTime',
      title: '创建时间',
      dataIndex: 'createTime',
      minWidth: 180,
      onCell: studentRowSpan,
    },
    {
      key: 'allowEditBasicInfo',
      title: '调整基本信息',
      minWidth: 120,
      onCell: studentRowSpan,
      render: (row) => (
        <Badge variant={row.allowEditBasicInfo === 1 ? 'success' : 'danger'}>
          {row.allowEditBasicInfo === 1 ? '允许' : '禁止'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      title: '操作',
      fixed: 'right',
      width: 72,
      onCell: studentRowSpan,
      render: (row) => (
        <DropdownMenu
          align="end"
          trigger={
            <IconButton
              variant="ghost"
              aria-label={`${row.studentName} 操作`}
              icon={<MoreHorizontal className="size-4" aria-hidden />}
            />
          }
          items={[
            {
              key: 'edit',
              label: '编辑',
              onSelect: () => {
                setEditing(studentsRef.current.get(row.studentId) ?? null)
                setFormOpen(true)
              },
            },
            {
              key: 'status',
              label: row.status === '0' ? '启用' : '禁用',
              danger: row.status !== '0',
              onSelect: () => {
                void handleToggleStatus(row)
              },
            },
            { key: 'div-1', type: 'divider' },
            {
              key: 'reset',
              label: '重置密码',
              onSelect: () => {
                void handleResetPassword(row)
              },
            },
            {
              key: 'recharge',
              label: '充值',
              onSelect: () => openPointsDialog(row, 'recharge'),
            },
            {
              key: 'deduct',
              label: '扣除',
              disabled: row.points <= 100,
              onSelect: () => openPointsDialog(row, 'deduct'),
            },
            {
              key: 'records',
              label: '充值记录',
              onSelect: () => {
                setRecordsTarget(row)
                setRecordsOpen(true)
              },
            },
            {
              key: 'basic',
              label:
                row.allowEditBasicInfo === 1
                  ? '禁止调整基本信息'
                  : '允许调整基本信息',
              disabled: !isMainAccount,
              onSelect: () => {
                void handleToggleBasicInfo(row)
              },
            },
            { key: 'div-2', type: 'divider' },
            {
              key: 'delete',
              label: '删除',
              danger: true,
              onSelect: () => {
                void handleDelete(row)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="学员管理"
        description="管理学员档案、点数与分科目学习信息。"
        extra={
          <Button
            icon={<Plus className="size-4" aria-hidden />}
            onClick={openCreate}
          >
            新增学员
          </Button>
        }
      />

      <div className="mt-4">
        <RequestTable
          ref={tableRef}
          request={fetchStudentList}
          beforeRequest={beforeRequest}
          afterRequest={afterRequest}
          columns={columns}
          searchForm={{
            initialValues: EMPTY_STUDENT_QUERY,
            collapsible: true,
            defaultExpanded: false,
            items: StudentSearchFields,
          }}
        />
      </div>

      {/* 新增 / 编辑学员弹窗 */}
      <StudentFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        onSubmit={handleFormSubmit}
      />

      {/* 充值 / 扣除点数弹窗 */}
      <StudentPointsDialog
        open={pointsOpen}
        onOpenChange={setPointsOpen}
        studentName={pointsTarget?.studentName}
        points={pointsTarget?.points}
        mode={pointsMode}
        onConfirm={handlePointsConfirm}
      />

      {/* 充值记录弹窗 */}
      <StudentRechargeRecordsDialog
        open={recordsOpen}
        onOpenChange={setRecordsOpen}
        student={recordsTarget}
      />

      <StudentBindTeacherDialog
        open={bindOpen}
        onOpenChange={setBindOpen}
        subjects={bindTarget?.subject}
        currentTeacherId={bindTarget?.teacherId}
        onConfirm={handleBindTeacherConfirm}
      />
    </>
  )
}
